import { useCallback, useEffect } from 'react';
import qs from 'query-string';

import useXHR from './useXHR';

/**
 * Custom XMLHttpRequest hook for GET requests
 * @param {Object} options
 * @param {string} options.url - The URL to fetch
 * @param {Record<string, any>} options.query - The query parameters
 * @param {boolean} options.onMount - Whether to send the request on mount
 * @param {string} options.responseType - The response type
 * @param {UseXHRConfiguration} options.configuration - The configuration object
 */
const useGet = ({ url, query, onMount, responseType, configuration }) => {
  const [state, req, setRes, , setOriginalRequest] = useXHR(
    onMount,
    configuration
  );
  const queryString = query ? qs.stringify(query) : null;

  const send = useCallback(
    ({ url: newUrl, query: newQuery } = {}) => {
      const latestUrl = newUrl || url;
      const latestQuery = newQuery ? qs.stringify(newQuery) : queryString;

      const urlToOpen = latestQuery ? `${latestUrl}?${latestQuery}` : latestUrl;

      setOriginalRequest({
        method: 'GET',
        url: urlToOpen,
        responseType,
      });
      req.open('GET', urlToOpen);
      if (responseType) {
        req.responseType = responseType;
      }

      const token = localStorage.getItem('token');
      if (token) {
        req.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      req.send();
    },
    [url, queryString, req, responseType, setOriginalRequest]
  );

  const cancel = useCallback(() => {
    req?.abort();
  }, [req]);

  useEffect(() => {
    if (onMount) {
      send();
    }
    return () => req.abort();
  }, [onMount, send, req]);

  return [state, send, setRes, cancel];
};

export default useGet;
